import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector, inject } from '@angular/core';
import { IBbitSelectDataRow } from '@bbitgmbh/bbit.utils';
import { BbitWebsiteService } from '@bbitgmbh/website.sdk';
import { take } from 'rxjs';
import { EditCompanionComponent } from './edit-companion/edit-companion.component';
import { EditCompanionOverlayRef } from './edit-companion/edit-companion.overlay-ref';

@Injectable({
  providedIn: 'root',
})
export class CompanionsService {
  private _overlay = inject(Overlay);
  private _website = inject(BbitWebsiteService);
  private _breakpointObserver = inject(BreakpointObserver);

  public async list(): Promise<IBbitSelectDataRow[]> {
    const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/companions`, {
      method: 'GET',
      headers: {
        Authorization: `${this._website.getAccessToken()}`,
      },
    });
    const data = await response.json();
    return data.companions;
  }

  public async addCompanion(): Promise<{ reload: boolean }> {
    return await this.editCompanion('');
  }

  public async editCompanion(_id: string): Promise<{ reload: boolean }> {
    return new Promise((resolve) => {
      const overlayConfig = new OverlayConfig({
        hasBackdrop: true,
        backdropClass: 'overlay-backdrop',
        panelClass: '',
        scrollStrategy: this._overlay.scrollStrategies.block(),
        positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
      });
      const overlayRef = this._overlay.create(overlayConfig);
      const overlayRefInstance = new EditCompanionOverlayRef(overlayRef);
      overlayRefInstance
        .observeClose()
        .pipe(take(1))
        .subscribe((reload) => {
          if (reload) {
            resolve({ reload });
          }
        });
      overlayRefInstance.setId(_id);
      const injector = Injector.create({
        providers: [{ provide: EditCompanionOverlayRef, useValue: overlayRefInstance }],
      });
      const containerPortal = new ComponentPortal(EditCompanionComponent, null, injector);
      overlayRef.attach(containerPortal);

      const breakpoint = this._breakpointObserver.observe(Breakpoints.XSmall).subscribe((size) => {
        if (size.matches) {
          overlayRef.updateSize({ width: '100%', height: '100%', maxHeight: '100%' });
        } else {
          overlayRef.updateSize({
            minWidth: '400px',
            maxWidth: '600px',
            width: '80%',
            height: '80%',
            maxHeight: '80%',
          });
        }
      });

      overlayRef.backdropClick().subscribe((): void => {
        breakpoint.unsubscribe();
        overlayRefInstance.close();
      });
    });
  }
}
